import React from 'react'
import { useTranslation, Trans } from 'react-i18next'
import { Layout } from 'ui/layout/Layout'
import { AppBarPatient } from 'ui/partials/AppBar'
import { H2, Text1 } from 'ui/shorthands/Texts'
import CenteredContainer from 'ui/layout/CenteredContainer'
import type { MedicalInstitutionProps } from 'types/applicant'
import type { VerificationTypes } from 'types/common'
import { Link } from '@mui/material'
import { makeStyles } from 'tss-react/mui'

const useStyles = makeStyles()(() => ({
  link: {
    fontWeight: 700,
  },
}))

export type ReceptionProps = {
  med: MedicalInstitutionProps
  title?: string
  description?: React.ReactNode
}

const ReceptionMessage: React.FC<ReceptionProps> = ({
  med,
  title,
  description,
}: ReceptionProps) => (
  <Layout header={<AppBarPatient>{med.name}</AppBarPatient>} showFooter>
    <CenteredContainer header={<H2>{title}</H2>}>
      {description && <Text1>{description}</Text1>}
    </CenteredContainer>
  </Layout>
)

/**
 * 無効な予約,診療科目がないなど
 */
export const ReceptionEmpty: React.FC<{ med: MedicalInstitutionProps }> = ({
  med,
}) => {
  const { t } = useTranslation()
  return <ReceptionMessage med={med} title={t('予約を受け付けておりません')} />
}

/**
 * メールからのリンク有効期限切れ
 */
export const ReceptionExpired: React.FC<{
  med: MedicalInstitutionProps
  receptionUrl: string
}> = ({ med, receptionUrl }) => {
  const { t } = useTranslation()
  const { classes } = useStyles()
  return (
    <ReceptionMessage
      med={med}
      title={t('有効期限が切れています')}
      description={
        <>
          <Trans
            i18nKey={
              'trans.もう一度<Link>こちら</Link>から予約にお進みください。'
            }
            components={{
              Link: <Link className={classes.link} href={receptionUrl} />,
            }}
          />
        </>
      }
    />
  )
}

/**
 * 仮予約有効期限切れ
 */
export const PreReservationExpired: React.FC<{
  med: MedicalInstitutionProps
}> = ({ med }) => {
  const { t } = useTranslation()
  return (
    <ReceptionMessage
      med={med}
      title={t('有効期限が切れています')}
      description={t('医療機関へお問い合わせください。')}
    />
  )
}
/**
 * 仮予約取り消しの時
 */
export const PreReservationNotFound: React.FC<{
  med: MedicalInstitutionProps
}> = ({ med }) => {
  const { t } = useTranslation()
  return (
    <ReceptionMessage
      med={med}
      title={t('仮予約が取消されました')}
      description={t('医療機関へお問い合わせください。')}
    />
  )
}

/**
 * 仮予約URLが予約済みの時
 */
export const PreReservationReserved: React.FC<{
  med: MedicalInstitutionProps
  type: VerificationTypes | undefined
}> = ({ med, type = 'email' }) => {
  const { t } = useTranslation()
  return (
    <ReceptionMessage
      med={med}
      title={t('すでに予約が完了しています')}
      description={t('{{label}}を確認してください', {
        label: type === 'sms' ? t('SMSアプリ') : t('メールボックス'),
      })}
    />
  )
}

/**
 * メールアドレス or SMS入力画面の送信後
 */
export const ReceptionSubmitted: React.FC<{
  med: MedicalInstitutionProps
  type: VerificationTypes | undefined
}> = ({ med, type = 'email' }) => {
  const { t } = useTranslation()
  return (
    <ReceptionMessage
      med={med}
      title={t('{{label}}を送信しました', {
        label: type === 'sms' ? t('SMS') : t('メール'),
      })}
      description={t('{{label}}を確認してください', {
        label: type === 'sms' ? t('SMSアプリ') : t('メールボックス'),
      })}
    />
  )
}

export default ReceptionMessage
